import { useId } from "react";
import type { PendingCombinedFile } from "@shared-core/data/api-types";
import {
  LEFT_OUT,
  tagToValue,
  useCombinedFileTagging,
  valueToTag,
  type PageTag,
  type SplitAssignment,
} from "@shared-core/domain/combined-file-tagging";
import { Button } from "../../app/components/primitives/button";
import { cn } from "../../app/components/primitives/utils";
import { PagePicture } from "./PagePicture";
import type { FetchFile } from "./useFetchedFile";

// "Which pages are which?" (doc 87 §6, the customer's lighter version): the one file they sent for every part, as a
// grid of page pictures, each with its number, its size and a menu of the parts, then "Not for printing". The
// sentence above the grid, each page's size, the parts and the "Not for printing" word are the read's; the customer
// checks the tags and confirms, and nothing is split before that.

const selectClass =
  "h-9 w-full rounded-md border border-input bg-input-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50";

export function PageTagging({
  pending,
  fetchFile,
  busy,
  error,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
}: {
  pending: PendingCombinedFile;
  /** Where a page picture is fetched from; null on a read-only page, where the pictures are numbers only. */
  fetchFile: FetchFile | null;
  busy: boolean;
  /** The owner's sentence for a refusal (a page tagged twice or left nowhere), shown beside the pages. */
  error: string | null;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: (request: SplitAssignment) => void;
  onCancel: () => void;
}) {
  const id = useId();
  const { tags, tag, request, untagged } = useCombinedFileTagging(pending);
  return (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (untagged.length === 0) onConfirm(request);
      }}
    >
      <h4 className="text-sm font-medium">Which pages are which?</h4>
      <p className="text-sm" data-testid="page-tagging-summary">
        {pending.summary}
      </p>
      <fieldset
        aria-describedby={error ? `${id}-error` : undefined}
        className="space-y-2"
      >
        <legend className="sr-only">Pages</legend>
        <ol className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {pending.pages.map((page) => {
            const pageTag: PageTag = tags[page.page_no] ?? null;
            const isLeftOut = pageTag === LEFT_OUT;
            return (
              <li
                key={page.page_no}
                data-page={page.page_no}
                data-tag={pageTag ?? ""}
                className="space-y-1"
              >
                <div className="relative">
                  <PagePicture
                    url={page.thumbnail_url}
                    fetchFile={fetchFile}
                    pageNo={page.page_no}
                    className={cn(isLeftOut && "opacity-40")}
                  />
                  {isLeftOut && (
                    <span className="absolute inset-x-0 bottom-1 mx-auto w-fit rounded bg-background/90 px-1.5 text-xs font-medium">
                      {pending.left_out_label}
                    </span>
                  )}
                </div>
                <p className="text-xs">
                  <span className="font-medium">Page {page.page_no}</span>
                  <span className="text-muted-foreground">
                    {" "}
                    · {page.size_label}
                  </span>
                </p>
                <select
                  className={selectClass}
                  aria-label={`Which part is page ${page.page_no}?`}
                  aria-invalid={error && pageTag === null ? true : undefined}
                  disabled={busy}
                  value={tagToValue(pageTag)}
                  onChange={(e) =>
                    tag(page.page_no, valueToTag(e.target.value))
                  }
                >
                  <option value="">Choose…</option>
                  {pending.parts.map((part) => (
                    <option key={part.part_key} value={part.part_key}>
                      {part.label}
                    </option>
                  ))}
                  <option value={LEFT_OUT}>{pending.left_out_label}</option>
                </select>
              </li>
            );
          })}
        </ol>
        {error && (
          <p
            id={`${id}-error`}
            role="alert"
            className="text-sm text-red-700 dark:text-red-400"
          >
            {error}
          </p>
        )}
      </fieldset>
      {untagged.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {untagged.length === 1 ? "Page" : "Pages"} {untagged.join(", ")}{" "}
          {untagged.length === 1 ? "needs" : "need"} a choice before you can
          confirm.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={busy || untagged.length > 0}>
          {confirmLabel}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={onCancel}
        >
          {cancelLabel}
        </Button>
      </div>
    </form>
  );
}
